import {
  Globe,
  Target,
  CurrencyDollar,
  TrendUp,
  Warning,
  Clock,
  Star,
} from "@phosphor-icons/react";
import { Badge } from "../ui/badge";
import { MetricCard } from "./MetricCard";
import { ConfidenceBreakdownCard } from "./ConfidenceBreakdownCard";
import { ExecuteNowCard } from "./ExecuteNowCard";
import { OpportunityAnalysis } from "./OpportunityAnalysis";
import { getWatchlistToggleClass, getTextPrimary, getTextSecondary, getCardClass } from "../../lib/styleHelpers";

const CATEGORY_ICONS = {
  "Cross-border Trade": Globe,
  "Currency Arbitrage": CurrencyDollar,
  "Real Estate": Target,
  "Commodities": Globe,
  "E-commerce": Target,
  "Technology": Globe,
  "Agriculture": Globe,
  "Energy": Globe,
};

export function OpportunityDetail({
  opportunity,
  getRiskColor,
  getRiskIconColor,
  getProbabilityColor,
  isDark,
  watchlistIds,
  onToggleWatchlist,
}) {
  const CategoryIcon = opportunity.category
    ? CATEGORY_ICONS[opportunity.category.split(" ")[0]] || Globe
    : Globe;

  return (
    <div className={`rounded-xl p-6 card-hover border ${getCardClass(isDark)} ${!isDark ? "shadow-sm" : ""}`} data-testid="opportunity-card">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDark ? "bg-purple-500/10 border border-purple-500/30" : "bg-purple-100 border border-purple-200"}`}>
            <CategoryIcon size={24} className="text-purple-500" />
          </div>
          <div>
            <Badge variant="outline" className={`mb-1 text-xs font-body ${isDark ? "border-purple-500/30 text-purple-300" : "border-purple-300 text-purple-600"}`}>
              {opportunity.category}
            </Badge>
            <h2 className={`text-xl sm:text-2xl font-bold tracking-tight ${getTextPrimary(isDark)}`}>
              {opportunity.title}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {opportunity.is_todays_opportunity && (
            <Badge className="bg-emerald-500 text-white">Today's Pick</Badge>
          )}
          <button
            onClick={() => onToggleWatchlist(opportunity)}
            className={`p-2 rounded-lg transition-all ${getWatchlistToggleClass(watchlistIds?.has(opportunity.id), isDark)}`}
            title={watchlistIds?.has(opportunity.id) ? "Remove from watchlist" : "Add to watchlist"}
            data-testid="watchlist-toggle-btn"
          >
            <Star size={22} weight={watchlistIds?.has(opportunity.id) ? "fill" : "regular"} />
          </button>
        </div>
      </div>

      <p className={`font-body text-base mb-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
        {opportunity.summary}
      </p>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard icon={Target} label="Probability" value={`${Math.round(opportunity.probability_score * 100)}%`} color={getProbabilityColor(opportunity.probability_score)} testId="metric-probability" isDark={isDark} />
        <MetricCard icon={TrendUp} label="Est. ROI" value={opportunity.estimated_roi} color="#10b981" testId="metric-roi" isDark={isDark} />
        <MetricCard icon={CurrencyDollar} label="Investment" value={opportunity.investment_required} color="#a855f7" testId="metric-investment" isDark={isDark} />
        <MetricCard icon={Clock} label="Horizon" value={opportunity.time_horizon} color="#8b5cf6" testId="metric-horizon" isDark={isDark} />
      </div>

      {/* Risk Level */}
      <div className={`flex items-center gap-4 mb-6 p-4 rounded-lg border ${isDark ? "bg-[#0c0c16] border-white/10" : "bg-gray-50 border-gray-200"}`}>
        <Warning size={24} className={getRiskIconColor(opportunity.risk_level)} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-body ${getTextSecondary(isDark)}`}>Risk Level:</span>
            <Badge className={getRiskColor(opportunity.risk_level)}>{opportunity.risk_level}</Badge>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {opportunity.risk_factors?.map((factor) => (
              <span key={factor} className={`text-xs px-2 py-1 rounded border ${isDark ? "text-gray-400 bg-[#0a0a14] border-white/10" : "text-gray-600 bg-white border-gray-200"}`}>
                {factor}
              </span>
            ))}
          </div>
        </div>
      </div>

      {opportunity.confidence_breakdown && <ConfidenceBreakdownCard breakdown={opportunity.confidence_breakdown} />}
      {opportunity.execution_guide && <ExecuteNowCard guide={opportunity.execution_guide} />}

      <OpportunityAnalysis opportunity={opportunity} isDark={isDark} />
    </div>
  );
}
